import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { storeApi } from "@/lib/storeApi";
import { pixel } from "@/lib/metaPixel";
import { useSalesOpen } from "@/pages/store/LaunchNotify";
import { SourcePicker } from "@/components/SourcePicker";

export const JOINED_KEY = "bgx_waitlist_joined";
const DISMISS_KEY = "bgx_popup_dismissed";
const HIDDEN = ["/account", "/notify", "/legal", "/store/checkout"];

export default function LaunchPopup() {
  const { pathname } = useLocation();
  const salesOpen = useSalesOpen();
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState("");
  const [sources, setSources] = useState([]);
  const [state, setState] = useState("idle");
  const [err, setErr] = useState("");
  const blocked = salesOpen !== false || HIDDEN.some((p) => pathname.startsWith(p));

  useEffect(() => {
    if (blocked || localStorage.getItem(JOINED_KEY) || sessionStorage.getItem(DISMISS_KEY)) return;
    const t = setTimeout(() => setShow(true), 5000);
    return () => clearTimeout(t);
  }, [blocked]);

  const close = () => { sessionStorage.setItem(DISMISS_KEY, "1"); setShow(false); };
  const already = () => { localStorage.setItem(JOINED_KEY, "1"); setShow(false); };
  const submit = async (e) => {
    e.preventDefault();
    setState("busy"); setErr("");
    try {
      await storeApi.post("/store/waitlist", { channel: "launch_popup", email, sources });
      localStorage.setItem(JOINED_KEY, "1");
      pixel.lead("launch_popup");
      setState("done");
    } catch (ex) { setErr(ex?.response?.data?.detail || "Couldn't add you - please check the email."); setState("idle"); }
  };

  if (!show || blocked) return null;
  return (
    <div data-testid="launch-popup" role="dialog" aria-modal="true" onClick={(e) => e.target === e.currentTarget && close()}
      onKeyDown={(e) => e.key === "Escape" && close()}
      className="fixed inset-0 z-[110] flex items-start pt-16 sm:items-center sm:pt-4 justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-md max-h-[88vh] overflow-y-auto rounded-2xl border border-[#FBBF24]/50 bg-[#050914] p-6 text-[#F1F5F9] shadow-2xl">
        <button onClick={close} aria-label="Close" data-testid="launch-popup-close" className="absolute top-1 right-1 p-3 text-[#64748B] hover:text-white"><X size={20} /></button>
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#FBBF24]">Launch list</div>
        <div className="text-2xl font-medium mt-2">Get notified when the store opens</div>
        <p className="text-sm text-[#94A3B8] mt-2">
          Leave your email and I'll let you know as soon as BotGenomix is live. Everyone on the launch list gets <b className="text-[#FBBF24]">20% off their first month</b> - just create your account with the same email when the store opens.
        </p>
        {state === "done" ? (
          <div data-testid="launch-popup-done" className="mt-5 text-sm font-semibold text-[#34D399]">
            You're on the list - I'll email you at launch.
            <span className="block mt-2 font-normal text-[#CBD5E1]">To get your 20% off, create your account with this same email when the store opens.</span>
            <button onClick={() => setShow(false)} data-testid="launch-popup-done-close" className="block mt-3 text-[#38BDF8] underline font-normal">Back to the site</button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-5 flex flex-col gap-2">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="launch-popup-email"
              className="bg-[#0B1220] border border-blue-900/60 rounded-lg px-4 py-3 text-sm focus:border-[#FBBF24] outline-none" />
            <SourcePicker value={sources} onChange={setSources} testid="launch-popup-source" />
            <button disabled={state === "busy"} data-testid="launch-popup-submit"
              className="mono text-[11px] uppercase tracking-widest px-6 py-3 rounded-lg bg-[#FBBF24] text-[#050914] font-semibold hover:bg-[#FDE68A] disabled:opacity-50">
              {state === "busy" ? "Adding…" : "Notify me"}
            </button>
          </form>
        )}
        {err && <div data-testid="launch-popup-error" className="mt-2 text-xs text-[#F87171]">{err}</div>}
        {state !== "done" && (
          <button onClick={already} data-testid="launch-popup-already" className="mt-4 text-xs text-[#94A3B8] underline hover:text-white">I've already signed up</button>
        )}
        <div className="text-[11px] text-[#64748B] mt-3">One email at launch plus occasional updates. Unsubscribe any time.</div>
      </div>
    </div>
  );
}
